import React, {useState} from 'react';
import {Button, Modal, Skeleton, Tree, message} from 'antd';
import {ApartmentOutlined, DeleteOutlined, EditOutlined, PlusOutlined, TeamOutlined} from '@ant-design/icons';
import {useQueryClient} from '@tanstack/react-query';
import {useOrganizationTree} from '../../hooks/useOrganizationTree';
import {organizationService} from '../../services/organization';
import {NameModal} from '../NameModal';
import './index.scss';

interface OrganizationTreeProps {
  rootId?: number | string;
  selectedId?: number | string;
  onSelect?: (node: any) => void;
}

export function OrganizationTree({
                                   rootId = 1,
                                   selectedId,
                                   onSelect,
                                 }: OrganizationTreeProps) {
  const queryClient = useQueryClient();
  const {data, isLoading} = useOrganizationTree(rootId);

  const [createOpen, setCreateOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [currentNode, setCurrentNode] = useState<any>(null);

  const treeData = (() => {
    if (!data) return [];

    const root = data.current_directory;
    const children = data.items
      ?.filter((item: any) => item.type === 'directory')
      ?.filter((item: any) => !item.is_private) || [];

    return [
      {
        title: renderTitle(root, true),
        raw: root,
        key: root.id,
        isRoot: true,
        children: children.map(buildTreeNode)
      }
    ];
  })();

  function buildTreeNode(node: any) {
    return {
      title: renderTitle(node, false),
      raw: node,
      key: node.id,
      isRoot: false,
      children: (node.children || []).map(buildTreeNode)
    };
  }

  function renderTitle(node: any, isRoot: boolean) {
    return (
      <div className="drive-org-tree__node">
        <span className="drive-org-tree__node-main">
          {isRoot ? (
            <ApartmentOutlined className="drive-org-tree__icon drive-org-tree__icon--root"/>
          ) : (
            <TeamOutlined className="drive-org-tree__icon"/>
          )}

          <span className="drive-org-tree__name">
            {node?.name}
          </span>
        </span>

        <span className="drive-org-tree__actions">
          {node.group_level < 4 && (
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined/>}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentNode(node);
                setCreateOpen(true);
              }}
            />
          )}

          {!isRoot && (
            <>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined/>}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentNode(node);
                  setRenameOpen(true);
                }}
              />

              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined/>}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDelete(node);
                }}
              />
            </>
          )}
        </span>
      </div>
    );
  }

  async function refreshTree() {
    await queryClient.invalidateQueries({queryKey: ['organization-tree', rootId]});
    await queryClient.invalidateQueries({queryKey: ['directory-tree', 'navigation']});
  }

  async function handleCreate(name: string) {
    if (!currentNode) {
      await organizationService.createMainDirectory({name});
    } else {
      await organizationService.createChildDirectory({
        parent_id: currentNode.id,
        name,
        visibility: 'edit',
        is_group: 1,
        group_level: currentNode.group_level + 1
      });
    }

    await refreshTree();
    setCreateOpen(false);
    setCurrentNode(null);
    void message.success('新建成功');
  }

  async function handleRename(name: string) {
    if (!currentNode) return;

    await organizationService.rename({
      item_id: currentNode.id,
      item_type: 'directory',
      new_name: name
    });

    await refreshTree();
    setRenameOpen(false);
    setCurrentNode(null);
    void message.success('重命名成功');
  }

  async function handleDelete(node: any) {
    if (node.children?.length) {
      message.warning('请先删除下级部门');
      return;
    }

    Modal.confirm({
      title: '确认删除？',
      content: `确定删除【${node.name}】吗？`,
      okText: '删除',
      okType: 'danger',
      onOk: async () => {
        const res = await organizationService.delete({
          item: [{
            id: node.id,
            type: 'directory'
          }]
        });
        await refreshTree();
        void message.success(res || '删除成功');
      }
    });
  }

  if (isLoading && !treeData.length) {
    return <Skeleton active paragraph={{rows: 8}}/>;
  }

  return (
    <>
      <div className="drive-org-tree">
        <Tree
          blockNode
          defaultExpandAll
          selectedKeys={selectedId != null ? [selectedId] : []}
          treeData={treeData}
          onSelect={(_, info: any) => {
            if (info.node.isRoot) {
              onSelect?.(null);
              return;
            }

            onSelect(info.node.raw);
          }}
        />
      </div>

      <NameModal
        open={createOpen}
        title={currentNode ? '新建子部门' : '新建组织'}
        placeholder="请输入名称"
        onCancel={() => {
          setCreateOpen(false);
          setCurrentNode(null);
        }}
        onOk={handleCreate}
      />

      <NameModal
        open={renameOpen}
        title="重命名"
        placeholder="请输入新名称"
        initialValue={currentNode?.name}
        onCancel={() => {
          setRenameOpen(false);
          setCurrentNode(null);
        }}
        onOk={handleRename}
      />
    </>
  );
}